import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, Truck, CheckCircle, Clock, Wrench, MapPin, Loader2, AlertCircle, Camera } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_ICONS = {
  confirmed: CheckCircle,
  in_production: Package,
  ready_to_ship: Package,
  shipped: Truck,
  delivered: MapPin,
  installed: Wrench,
};

export default function OrderTrackingPage() {
  const { code } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    axios.get(`${API}/api/public/order-track/${code}`)
      .then(res => { setOrder(res.data); setError(null); })
      .catch(err => setError(err.response?.data?.detail || 'Order not found'))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Order Not Found</h2>
          <p className="text-sm text-neutral-500">{error || 'The tracking link may be incorrect or expired.'}</p>
        </div>
      </div>
    );
  }

  const { progress } = order;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="order-tracking-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white">
        <div className="max-w-2xl mx-auto px-4 py-6 text-center">
          <h1 className="text-xl font-bold tracking-wider mb-1" style={{ fontFamily: 'Georgia, serif' }}>APOLLO SHADES</h1>
          <p className="text-amber-500 text-xs tracking-wide">ORDER TRACKING</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Order Info */}
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs text-neutral-400 uppercase tracking-wide">Order Number</p>
              <p className="text-lg font-bold text-neutral-800" data-testid="tracking-so-number">{order.so_number}</p>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full font-medium ${
              order.status === 'completed' || order.status === 'delivered' || order.status === 'installed' ? 'bg-green-100 text-green-700' :
              order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
              'bg-amber-100 text-amber-700'
            }`} data-testid="tracking-status">
              {order.status.replace(/_/g, ' ')}
            </span>
          </div>
          {order.customer_name && <p className="text-sm text-neutral-500">For: {order.customer_name}</p>}
          {order.created_at && <p className="text-xs text-neutral-400 mt-1">Placed: {new Date(order.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>}
        </div>

        {/* Progress Timeline */}
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-neutral-700 mb-4">Order Progress</h3>
          <div className="relative" data-testid="tracking-progress">
            {progress.steps.map((step, i) => {
              const Icon = STEP_ICONS[step.key] || Clock;
              const isActive = i === progress.current_step;
              const isDone = step.completed;
              return (
                <div key={step.key} className="flex items-start gap-3 relative">
                  {/* Connector line */}
                  {i < progress.steps.length - 1 && (
                    <div className={`absolute left-[15px] top-[30px] w-0.5 h-[calc(100%-6px)] ${isDone ? 'bg-green-400' : 'bg-neutral-200'}`} />
                  )}
                  {/* Icon */}
                  <div className={`relative z-10 w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 ${
                    isDone ? 'bg-green-500 text-white' : isActive ? 'bg-amber-500 text-white ring-4 ring-amber-100' : 'bg-neutral-100 text-neutral-300'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  {/* Label */}
                  <div className={`pb-6 ${isDone || isActive ? '' : 'opacity-50'}`}>
                    <p className={`text-sm font-medium ${isDone ? 'text-green-700' : isActive ? 'text-amber-700' : 'text-neutral-400'}`}>{step.name}</p>
                    {isActive && <p className="text-xs text-amber-600 mt-0.5">Current status</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Installation Date */}
        {order.installation_date && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 shadow-sm" data-testid="tracking-install-date">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Wrench className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  Installation: {new Date(order.installation_date + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                {order.assigned_installer_name && (
                  <p className="text-xs text-amber-600 mt-0.5">Installer: {order.assigned_installer_name}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Items */}
        {order.items && order.items.length > 0 && (
          <div className="bg-white rounded-xl border p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-neutral-700 mb-3">Items ({order.item_count})</h3>
            <div className="space-y-2" data-testid="tracking-items">
              {order.items.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                  <span className="font-medium">{item.room}</span>
                  <span className="text-neutral-300">|</span>
                  <span>{item.width}" x {item.height}"</span>
                  {item.fabric && <><span className="text-neutral-300">|</span><span>{item.fabric}</span></>}
                  {item.mount && <><span className="text-neutral-300">|</span><span className="text-xs">{item.mount}</span></>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delivery Photos */}
        {order.delivery_photos && order.delivery_photos.length > 0 && (
          <div className="bg-white rounded-xl border p-5 shadow-sm" data-testid="tracking-photos">
            <h3 className="text-sm font-semibold text-neutral-700 mb-3 flex items-center gap-2">
              <Camera className="w-4 h-4 text-green-600" /> Completion Photos
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {order.delivery_photos.map((photo, i) => (
                <div key={i} className="rounded-lg overflow-hidden border">
                  <img src={`${API}${photo.url}`} alt={photo.caption || 'Completion photo'} className="w-full h-40 object-cover" />
                  {photo.caption && <p className="text-xs text-neutral-500 p-2">{photo.caption}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-neutral-400 pt-4">
          <p>Questions about your order? Call <a href="tel:+12122090654" className="text-amber-600 hover:underline">(212) 209-0654</a></p>
          <p className="mt-1">Apollo Shades &mdash; 1 Brooklyn Navy Yard, Building 33, Suite 201B, Brooklyn, NY 11205</p>
        </div>
      </div>
    </div>
  );
}
